import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  CreateVirtualKeyRequest,
  CreateVirtualKeyResponse,
  GatewayModel,
  ListGatewayModelsResponse,
  ListVirtualKeysResponse,
  RevokeVirtualKeyResponse,
  UsageSummary,
  UsageWindow,
  VirtualKey,
  VirtualKeyUsage,
} from '@internal/backstage-plugin-ai-gateway-keys-common';
import { AiGatewayKeysApi } from './AiGatewayKeysApi';

export class AiGatewayKeysClient implements AiGatewayKeysApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async listModels(): Promise<GatewayModel[]> {
    const response = await this.request<ListGatewayModelsResponse>('/models');
    return response.items;
  }

  async listKeys(): Promise<VirtualKey[]> {
    const response = await this.request<ListVirtualKeysResponse>('/keys');
    return response.items;
  }

  async createKey(request: CreateVirtualKeyRequest): Promise<CreateVirtualKeyResponse> {
    return this.request<CreateVirtualKeyResponse>('/keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  }

  async revokeKey(keyId: string): Promise<VirtualKey> {
    const response = await this.request<RevokeVirtualKeyResponse>(`/keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    });
    return response.key;
  }

  async getUsage(keyId: string, window: UsageWindow): Promise<VirtualKeyUsage> {
    return this.request<VirtualKeyUsage>(`/keys/${encodeURIComponent(keyId)}/usage?window=${window}`);
  }

  async getUsageSummary(window: UsageWindow): Promise<UsageSummary> {
    return this.request<UsageSummary>(`/usage/summary?window=${window}`);
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-gateway-keys');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return (await response.json()) as T;
  }
}
